import React, { useState } from 'react';
import { MessageSquare, Shield, AlertTriangle, Rocket, CheckCircle, Github, Mail, Linkedin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowSuccessToast(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setShowSuccessToast(false), 5000);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    { icon: Mail, text: 'harms.tobias89@gmail.com', href: 'mailto:harms.tobias89@gmail.com' },
    { icon: Github, text: 'github.com/tobiasharms-sec', href: 'https://github.com/tobiasharms-sec' },
    { icon: Linkedin, text: 'linkedin.com/in/tobiasharms', href: 'https://linkedin.com/in/tobiasharms' }
  ];

  return (
    <div className="min-h-screen bg-black pt-16 relative overflow-hidden">
      {showSuccessToast && (
        <div className="fixed top-20 right-4 z-50 bg-gray-900 border border-green-500 rounded-lg p-4 shadow-lg shadow-green-500/25 animate-pulse">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div>
              <div className="text-green-400 font-mono text-sm font-bold">MESSAGE.TRANSMITTED</div>
              <div className="text-gray-300 font-mono text-xs">Encrypted communication received. Response ETA: 24h</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4 font-mono">
            <span className="text-green-400">&gt;</span> ESTABLISH.CONNECTION
          </h1>
          <p className="text-gray-400 font-mono max-w-3xl mx-auto">
            [ SECURE COMMUNICATION CHANNEL ]<br/>
            POV collaboration | Technical partnerships | Security advisory
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gray-900/50 border border-green-500/30 rounded-xl p-8 backdrop-blur-sm">
            <h2 className="text-2xl font-bold text-green-400 mb-6 font-mono flex items-center gap-2">
              <MessageSquare className="w-6 h-6" />
              SALES.ENGINEERING.INQUIRY
            </h2>
            <p className="text-sm text-gray-400 mb-6">
              For POV collaboration, technical partnerships, or speaking engagements
            </p>
            <form onSubmit={handleSubmit} className="space-y-6">
              <input type="hidden" name="form-name" value="contact" />
              {['name', 'email', 'message'].map((field) => (
                <div key={field}>
                  <label htmlFor={field} className="block text-sm font-mono text-green-400 mb-2">
                    {field === 'name' ? 'SENDER.IDENTITY' : field === 'email' ? 'CONTACT.PROTOCOL' : 'MESSAGE.PAYLOAD'}
                  </label>
                  {field === 'message' ? (
                    <textarea
                      id={field}
                      name={field}
                      rows="5"
                      value={formData[field]}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white font-mono disabled:opacity-50"
                      placeholder={field === 'message' ? 'Enter your message content...' : `Enter your ${field}...`}
                    />
                  ) : (
                    <input
                      type={field === 'email' ? 'email' : 'text'}
                      id={field}
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white font-mono disabled:opacity-50"
                      placeholder={field === 'email' ? 'secure@domain.com' : 'Enter your identifier...'}
                    />
                  )}
                </div>
              ))}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-black py-3 rounded-lg font-bold font-mono transition-colors shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    TRANSMITTING...
                  </>
                ) : (
                  'TRANSMIT.MESSAGE'
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-gray-900/50 border border-blue-500/30 rounded-xl p-8 backdrop-blur-sm">
              <h2 className="text-2xl font-bold text-blue-400 mb-6 font-mono flex items-center gap-2">
                <Shield className="w-6 h-6" />
                CONNECTION.PROTOCOLS
              </h2>
              <div className="space-y-4">
                {contactInfo.map(({ icon: Icon, text, href }) => (
                  <a key={text} href={href} className="flex items-center gap-4 text-gray-300 hover:text-green-400 transition-colors font-mono" target="_blank" rel="noopener noreferrer">
                    <Icon className="w-5 h-5" />
                    {text}
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-gray-900/50 border border-red-500/30 rounded-xl p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-red-400 mb-4 font-mono flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                RESPONSE.PROTOCOL
              </h3>
              <p className="text-gray-300 font-mono text-sm leading-relaxed">
                [STANDARD_RESPONSE_TIME] → 24 hours<br/>
                [PRIORITY_CHANNEL] → LinkedIn for technical matters<br/>
                [AVAILABILITY] → Active business hours EST<br/>
                [PREFERRED_TOPICS] → POVs, Integrations, Architecture
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-xl p-6 text-center">
              <Shield className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <div className="text-green-400 font-mono text-sm">SECURITY.VERIFIED</div>
              <div className="text-gray-400 font-mono text-xs mt-2">
                OSCP.CERTIFIED | CRTO.CERTIFIED | VECTRA.AI
              </div>
            </div>

            <div className="bg-gray-900/30 border border-purple-500/30 rounded-xl p-6">
              <h4 className="text-purple-400 font-mono text-sm mb-3 flex items-center gap-2">
                <Rocket className="w-4 h-4" />
                ENGAGEMENT.TYPES
              </h4>
              <div className="text-gray-400 font-mono text-xs space-y-1">
                <div>• POV Strategy & Execution</div>
                <div>• Security Architecture Reviews</div>
                <div>• Technical Enablement & Training</div>
                <div>• Conference Speaking (Pre-Sales Focus)</div>
                <div>• Customer Success Workshops</div>
                <div>• Integration Best Practices</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;